import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { Box } from 'src/components/Box';
import { Typography } from 'src/components/Typography';
import { Button } from 'src/components/Button';

import { CreateJob } from './CreateJobForm';
import { CreateRole } from './CreateRoleForm';
import { InviteWorkersToJob } from './InviteWorkersToJob';
import { NewJobData } from 'src/api/hooks/useJob.types';
import { ConfirmJobDetails } from './ConfirmJobDetails';
import { WorkerProfile } from 'src/DTO/WorkerProfile.type';
import { useCreateJob, useCreateJobOfferBatch } from 'src/api/hooks/useJob';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { CreateRoleForm } from './CreateRoleForm.types';
import { useCreateRole } from 'src/api/hooks/useRole';
import { Role } from 'src/DTO/Role.type';
import { Job, JobWage } from 'src/DTO/Job.type';
import { CreateJobStep1 } from './CreateJobStep1';
import { CreateJobStep2 } from './CreateJobStep2';
import { CreateJobStep4 } from './CreateJobStep4';
import { CreateJobStep5 } from './CreateJobStep5';
import { EditModes } from '../Job.types';

import { TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { getInitialMobilePreviewData, MobilePreview, MobilePreviewData } from './MobilePreview';

const CREATE_JOB_STEPS = [1, 2, 3, 4, 5, 6, 7, 8];

export interface CreateJobDialogProps {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  editRoleData?: Role;
  editJobData?: Job;
  editMode?: EditModes;
  onSuccessCallback?: (job: Job) => void;
  onCancel?: () => void;
}

export const CreateJobDialog = ({
  open,
  setOpen,
  editMode,
  editRoleData,
  editJobData,
  onSuccessCallback,
  onCancel,
}: CreateJobDialogProps) => {
  const { t } = useTranslation();

  const jobRole = { ...editJobData, role: editRoleData } as NewJobData;

  /* LOCAL STATE */
  const [activeStep, setActiveStep] = useState(0);
  const [newJobData, setNewJobData] = useState<NewJobData>(editJobData ?? ({} as NewJobData));
  const [newRoleData, setNewRoleData] = useState<CreateRoleForm | Role>(
    editRoleData ?? ({} as CreateRoleForm),
  );
  const [selectedWorkers, setSelectedWorkers] = useState<WorkerProfile[]>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);
  const [wageInfo, setWageInfo] = useState<JobWage>();
  const [mobilePreviewData, setMobilePreviewData] = useState<MobilePreviewData>(() =>
    getInitialMobilePreviewData(jobRole),
  );

  /* GLOBAL STATE */
  const setJob = useStoreActions(actions => actions.jobs.setJob);
  const setRole = useStoreActions(actions => actions.jobs.setRole);
  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const handleSuccessMessage = () => {
    const message = editMode
      ? t('jobPage.createJobForm.editSuccess')
      : [
          t('jobPage.createJobForm.success'),
          selectedWorkers.length > 0 && t('jobPage.createJobForm.inviteAfterActivation'),
        ]
          .filter(Boolean)
          .join('. ');

    setPopUpData({
      isOpen: true,
      type: PopUpEnum.success,
      message,
    });
  };

  const handleErrorMessage = () => {
    setPopUpData({
      isOpen: true,
      type: PopUpEnum.error,
      message: !!editMode ? t('jobPage.createJobForm.editError') : t('jobPage.createJobForm.error'),
    });
  };

  const reset = () => {
    const jobData = (editMode && editJobData) || ({} as NewJobData);
    const roleData = (editMode && editRoleData) || ({} as CreateRoleForm);

    setActiveStep(0);
    setNewJobData(jobData);
    setNewRoleData(roleData);
    setSelectedWorkers([]);
    setMobilePreviewData(getInitialMobilePreviewData(editMode ? jobData : undefined));
  };

  const handleShowCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(true);
  };

  const handleClose = (shouldTriggerCancel = true) => {
    reset();
    setIsConfirmDialogOpen(false);
    setOpen(false);
    if (shouldTriggerCancel) {
      !!onCancel && onCancel();
    }
  };

  const [createRole] = useCreateRole({
    onComplete: newRole => {
      if (newRole) {
        setNewRoleData(newRole);
        setRole(newRole);
        handleCreateJob(newRole);
      }
    },
    onError: res => {
      console.log('failed', res);
    },
  });

  const [createJobOfferBatch, { loading: inviteWorkersToJobLoading }] = useCreateJobOfferBatch({
    onComplete: () => {
      handleSuccessMessage();
      handleClose(false);
    },
    onError: () => {
      handleErrorMessage();
      handleClose(false);
    },
  });

  const [createJob] = useCreateJob({
    onComplete: createdJob => {
      if (createdJob) {
        setJob(createdJob);

        if (selectedWorkers.length > 0) {
          createJobOfferBatch({
            variables: {
              offers: selectedWorkers.map(worker => ({
                workerId: worker.id,
                jobId: createdJob.id,
                name: worker.name,
                phoneNumber: worker.phoneNumber,
              })),
            },
          });
        } else {
          handleSuccessMessage();
          handleClose(false);
        }

        !!onSuccessCallback && onSuccessCallback(createdJob);
      }
    },
    onError: () => {
      handleErrorMessage();
    },
  });

  const handleNavigateFromRoleStep = (roleData: CreateRoleForm) => {
    setNewRoleData(roleData);
    handleMoveToNextStep();
  };

  const handleNavigateFromJobStep = (jobData: NewJobData, wage: JobWage) => {
    setNewJobData(jobData);
    setWageInfo(wage);
    handleMoveToNextStep();
  };

  const handleNavigateFromJobStep1 = (jobData: NewJobData) => {
    setNewJobData(jobData);
    handleMoveToNextStep();
  };

  const handleMoveToNextStep = () => {
    setActiveStep(activeStep + 1);
  };

  const handleMoveToPreviousStep = () => {
    setActiveStep(activeStep - 1);
  };

  const handleCreateJob = (role: CreateRoleForm) => {
    if (!newJobData || !role.id) {
      return;
    }

    /* eslint-disable indent */
    const dataToSave = !!editMode
      ? {
          id: editJobData?.id,
          roleId: role.id,
          ...newJobData,
          genderTypes: newJobData.genderTypes?.join(','),
        }
      : {
          roleId: role.id,
          ...newJobData,
          startDate: new Date().toISOString(),
          genderTypes: newJobData.genderTypes?.join(','),
        };
    /* eslint-enable indent */

    createJob({
      variables: dataToSave,
    });
  };

  const handleCreateRole = () => {
    if (!newRoleData) {
      return;
    }

    createRole({
      variables: {
        name: newRoleData.name,
        description: newRoleData.description,
        feor: newRoleData.feor,
        companyProfile: newRoleData.companyProfile,
        postCode: newRoleData.postCode,
        city: newRoleData.city,
        address: newRoleData.address,
        localizedData: newRoleData.localizedData,
        county: newRoleData.county,
        feedSourceUrl: newRoleData.feedSourceUrl,
      },
    });
  };

  const handleStartCreateJobProcess = () => {
    if (!newRoleData?.id) {
      handleCreateRole();
    } else {
      createRole({ variables: { ...newRoleData } });
    }
  };

  function getStepContent() {
    switch (activeStep) {
      case 0:
        return (
          <CreateRole
            onCancel={handleShowCloseConfirmDialog}
            onSubmit={handleNavigateFromRoleStep}
            roleId={newRoleData?.id}
            newRoleData={Object.keys(newRoleData || {}).length > 0 ? newRoleData : undefined}
            editMode={editMode}
            newJobData={newJobData}
            setMobilePreviewData={setMobilePreviewData}
          />
        );
      case 1:
        if (newJobData) {
          return (
            <CreateJobStep1
              handleNextButtonClick={handleNavigateFromJobStep1}
              handlePreviousButtonClick={handleMoveToPreviousStep}
              newJobData={newJobData}
              editMode={editMode}
              setMobilePreviewData={setMobilePreviewData}
            />
          );
        }
        return null;
      case 2:
        if (newJobData) {
          return (
            <CreateJobStep2
              handleNextButtonClick={handleNavigateFromJobStep1}
              handlePreviousButtonClick={handleMoveToPreviousStep}
              newJobData={newJobData}
              editMode={editMode}
              setMobilePreviewData={setMobilePreviewData}
            />
          );
        }
        return null;
      case 3:
        return (
          <CreateJob
            handleNextButtonClick={handleNavigateFromJobStep}
            handlePreviousButtonClick={handleMoveToPreviousStep}
            newJobData={newJobData}
            editMode={editMode}
            setMobilePreviewData={setMobilePreviewData}
          />
        );
      case 4:
        if (newJobData) {
          return (
            <CreateJobStep4
              handleNextButtonClick={handleNavigateFromJobStep1}
              handlePreviousButtonClick={handleMoveToPreviousStep}
              newJobData={newJobData}
              setMobilePreviewData={setMobilePreviewData}
            />
          );
        }
        return null;
      case 5:
        if (newJobData) {
          return (
            <CreateJobStep5
              handleNextButtonClick={handleNavigateFromJobStep1}
              handlePreviousButtonClick={handleMoveToPreviousStep}
              newJobData={newJobData}
              editMode={editMode}
            />
          );
        }
        return null;
      case 6:
        if (newJobData) {
          return (
            <InviteWorkersToJob
              handleNextButtonClick={handleNavigateFromJobStep1}
              handlePreviousButtonClick={handleMoveToPreviousStep}
              selectedWorkers={selectedWorkers}
              setSelectedWorkers={setSelectedWorkers}
              newJobData={newJobData}
            />
          );
        }

        return null;
      case 7:
        return (
          <ConfirmJobDetails
            handleSubmit={handleStartCreateJobProcess}
            handlePreviousButtonClick={handleMoveToPreviousStep}
            role={newRoleData}
            selectedWorkers={selectedWorkers}
            newJobData={newJobData}
            inviteWorkersToJobLoading={inviteWorkersToJobLoading}
            isEditMode={!!editMode}
            wage={wageInfo}
          />
        );
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleShowCloseConfirmDialog}
        aria-labelledby="form-dialog-title"
        fullScreen>
        <DialogContent>
          <Box display="flex" justifyContent="flex-end" onClick={handleShowCloseConfirmDialog}>
            <Close style={{ cursor: 'pointer' }} />
          </Box>
          <Box pt={3} pb={8}>
            <Grid container justifyContent="center">
              <Grid item xs={12} lg={8}>
                <Box mt={6} mb={1} textAlign="center">
                  <Box mb={1}>
                    <Typography variant="h3" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                      {!!editMode && <Trans>jobPage.editJob</Trans>}
                      {!editMode && <Trans>jobPage.createJobTitle</Trans>}
                    </Typography>
                  </Box>
                </Box>

                <Stepper activeStep={activeStep} alternativeLabel>
                  {CREATE_JOB_STEPS.map(label => {
                    return (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
                <Card sx={{ flexDirection: 'row' }}>
                  <Box p={4}>
                    <Box>{getStepContent()}</Box>
                  </Box>
                  <Box p={4} display={{ xs: 'none', xxl: 'block' }}>
                    <Box>{<MobilePreview jobData={mobilePreviewData} editMode={editMode} />}</Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}>
        <DialogTitle>
          <Trans>jobPage.createJobForm.confirmCloseTitle</Trans>
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="textPrimary">
            <Trans>jobPage.createJobForm.confirmCLoseText</Trans>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDialogOpen(false)}>
            <Trans>generic.no</Trans>
          </Button>
          <Button onClick={handleClose} variant={BUTTON_VARIANT.GRADIENT} color={BUTTON_COLOR.DARK}>
            <Trans>generic.yes</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
